import React, { useEffect, useState } from 'react'
import {
  Redirect,
  useHistory,
  useLocation,
  useParams
} from 'react-router-dom'
import { Col, Container, Row } from 'react-bootstrap'

import useHeaderTitle from 'utils/hooks/useHeaderTitle'

import Toast from 'components/Toast'

import { Routes } from 'global/routes'
import { useFeatureFlags } from 'global/hooks/useFeatureFlags'
import {
  useIntegrationProviders,
  useMyIntegrations
} from 'global/hooks/useIntegrations'
import { useTranscripts } from 'global/hooks/useTranscripts'
import {
  useCreateLeadershipProfile,
  useLeadershipProfile,
  useMyLeadershipProfiles
} from 'global/hooks/useLeadershipProfiles'

import { FEATURE_FLAG_KEYS } from 'global/hooks/useFeatureFlags/constants'

import Button from 'components/Button'
import PageLoader from 'components/PageLoader'

import GenerationProgress from './GenerationProgress'
import ProfileCard from './ProfileCard'
import ProfileDetail from './ProfileDetail'
import IntegrationStatusBar from './IntegrationStatusBar'
import GenerateProfileModal from './GenerateProfileModal'

const LeadershipInAction = () => {
  useHeaderTitle('Leadership in Action')

  const {
    isEnabled,
    isLoading: flagsLoading
  } = useFeatureFlags()
  const {
    providers,
    isLoading: providersLoading
  } = useIntegrationProviders()
  const {
    integrations,
    isLoading: integrationsLoading,
    refetch: refetchIntegrations,
    connect,
    isConnecting,
    connectError,
    resetConnect,
    disconnect,
    isDisconnecting,
    startOAuth
  } = useMyIntegrations()

  // Read OAuth callback query params on entry. On success, refetch
  // integrations so the new connection shows up. Strip the params from
  // the URL so a refresh doesn't re-toast.
  const location = useLocation()
  const history = useHistory()
  const [oauthToast, setOauthToast] = useState(null)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const connected = params.get('integration_connected')
    const errorMsg = params.get('integration_error')
    const integrationKey = params.get('integration')
    if (!connected && !errorMsg) return
    if (connected === 'true') {
      const niceName = integrationKey
        ? integrationKey.charAt(0).toUpperCase() +
          integrationKey.slice(1)
        : null
      setOauthToast({
        variant: 'primary',
        title: niceName
          ? `${niceName} connected`
          : 'Integration connected',
        body: niceName
          ? `${niceName} is connected. You can now generate a profile from your meetings.`
          : 'You can now generate a profile.'
      })
      refetchIntegrations()
    } else if (errorMsg) {
      setOauthToast({
        variant: 'danger',
        title: 'Connection failed',
        body: errorMsg
      })
    }
    params.delete('integration_connected')
    params.delete('integration_error')
    params.delete('integration')
    history.replace({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : ''
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const connectedProviders = providers.filter(p =>
    integrations.some(i => i.provider === p.key)
  )
  const activeProvider = connectedProviders[0] || null

  const {
    transcripts,
    isLoading: transcriptsLoading,
    error: transcriptsError,
    refetch: refetchTranscripts
  } = useTranscripts(
    activeProvider ? activeProvider.key : null,
    {
      enabled: Boolean(activeProvider)
    }
  )

  // Deep link support: /leadership-in-action/profiles/:profileId hydrates
  // directly into the detail view. The URL is the source of truth; state
  // mirrors it so View/Back can stay synchronous through history.push.
  const { profileId: profileIdFromUrl } = useParams()

  const [activeProfileId, setActiveProfileId] = useState(null)
  const [viewingProfileId, setViewingProfileId] = useState(
    profileIdFromUrl || null
  )
  const [showGenerateModal, setShowGenerateModal] = useState(
    false
  )

  useEffect(() => {
    setViewingProfileId(profileIdFromUrl || null)
    if (profileIdFromUrl) setActiveProfileId(null)
  }, [profileIdFromUrl])

  const {
    create,
    isCreating,
    createError,
    reset: resetCreate
  } = useCreateLeadershipProfile()

  const {
    profiles,
    isLoading: profilesLoading
  } = useMyLeadershipProfiles()

  // If the user navigates away while a profile is being generated and comes
  // back, the local activeProfileId is gone. Re-attach to any in-flight
  // profile in the list so they see the progress bar again instead of just
  // a "Generating" card in the grid.
  useEffect(() => {
    if (activeProfileId) return
    if (viewingProfileId) return
    const inFlight = (profiles || []).find(
      p => p.status === 'queued' || p.status === 'running'
    )
    if (inFlight) setActiveProfileId(inFlight.id)
  }, [profiles, activeProfileId, viewingProfileId])

  const {
    profile: viewingProfile,
    isLoading: viewingProfileLoading
  } = useLeadershipProfile(viewingProfileId, {
    pollIntervalMs: 0,
    enabled: Boolean(viewingProfileId)
  })

  const openGenerateModal = () => {
    resetCreate()
    setShowGenerateModal(true)
  }
  const closeGenerateModal = () => {
    setShowGenerateModal(false)
  }

  const handleGenerate = async transcriptIds => {
    if (!activeProvider) return
    const profile = await create({
      provider: activeProvider.key,
      transcriptIds
    })
    if (profile && profile.id) {
      setShowGenerateModal(false)
      setActiveProfileId(profile.id)
    }
  }

  const handleStartOver = () => {
    setActiveProfileId(null)
    resetCreate()
  }

  const handleViewProfile = id => {
    history.push(`/leadership-in-action/profiles/${id}`)
  }

  const handleBackToList = () => {
    history.push(Routes.LEADERSHIP_IN_ACTION)
  }

  if (flagsLoading) {
    return (
      <PageLoader loader>
        <div />
      </PageLoader>
    )
  }

  if (!isEnabled(FEATURE_FLAG_KEYS.LEADERSHIP_IN_ACTION)) {
    return <Redirect to={Routes.DASHBOARD} />
  }

  if (viewingProfileId) {
    return (
      <Container className="py-30">
        <Row>
          <Col xs={12} lg={11} xl={10}>
            {viewingProfileLoading || !viewingProfile ? (
              <p>Loading profile…</p>
            ) : (
              <ProfileDetail
                profile={viewingProfile}
                onBack={handleBackToList}
              />
            )}
          </Col>
        </Row>
      </Container>
    )
  }

  const isLoadingShell = providersLoading || integrationsLoading
  const hasIntegration = Boolean(activeProvider)
  const hasProfiles = (profiles || []).length > 0

  const renderEmptyState = () => {
    if (!hasIntegration) {
      return (
        <div className="p-30 border rounded bg-gray-light text-center">
          <p
            className="font-weight-bold mb-10"
            style={{ fontSize: 18 }}
          >
            Discover how you lead, in your real meetings
          </p>
          <p
            className="text-gray-dark mb-20"
            style={{ maxWidth: 540, margin: '0 auto' }}
          >
            Connect a transcript source below to pull in your
            meetings. We&apos;ll analyze how you show up across
            10 leadership competencies and build a profile from
            your real conversations.
          </p>
          <p
            className="text-gray-dark mb-0"
            style={{ fontSize: 13 }}
          >
            ↓ Start by connecting a source
          </p>
        </div>
      )
    }
    return (
      <div className="p-30 border rounded bg-gray-light text-center">
        <p
          className="font-weight-bold mb-10"
          style={{ fontSize: 18 }}
        >
          You&apos;re connected — let&apos;s build your first
          profile
        </p>
        <p
          className="text-gray-dark mb-20"
          style={{ maxWidth: 540, margin: '0 auto' }}
        >
          Pick 3–10 recent meetings where you led — coaching,
          decisions, planning, hard feedback — and we&apos;ll
          generate a profile from them.
        </p>
        <Button
          variant="secondary"
          smallPadding
          onClick={openGenerateModal}
        >
          + Generate first profile
        </Button>
      </div>
    )
  }

  return (
    <Container className="py-30">
      <Row>
        <Col xs={12} lg={11} xl={10}>
          {activeProfileId && (
            <div className="mb-30">
              <GenerationProgress
                profileId={activeProfileId}
                onStartOver={handleStartOver}
                onViewProfile={handleViewProfile}
              />
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center mb-15">
            <h2 className="mb-0" style={{ fontSize: 20 }}>
              Your profiles
            </h2>
            {hasIntegration && (
              <Button
                variant="secondary"
                smallPadding
                onClick={openGenerateModal}
              >
                + New profile
              </Button>
            )}
          </div>

          {isLoadingShell || profilesLoading ? (
            <p>Loading…</p>
          ) : hasProfiles ? (
            <Row>
              {profiles.map(profile => (
                <Col
                  key={profile.id}
                  xs={12}
                  sm={6}
                  lg={4}
                  className="mb-20"
                >
                  <ProfileCard
                    profile={profile}
                    onSelect={handleViewProfile}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            renderEmptyState()
          )}

          <IntegrationStatusBar
            providers={providers}
            integrations={integrations}
            isLoading={isLoadingShell}
            onConnect={connect}
            onDisconnect={disconnect}
            onStartOAuth={startOAuth}
            isConnecting={isConnecting}
            isDisconnecting={isDisconnecting}
            connectError={connectError}
            onResetConnectError={resetConnect}
          />
        </Col>
      </Row>

      <GenerateProfileModal
        show={showGenerateModal}
        onClose={closeGenerateModal}
        providerName={
          activeProvider ? activeProvider.name : null
        }
        transcripts={transcripts}
        isLoading={transcriptsLoading}
        error={transcriptsError}
        onRetry={refetchTranscripts}
        onGenerate={handleGenerate}
        isGenerating={isCreating}
        generateError={createError}
      />

      <Toast
        show={Boolean(oauthToast)}
        toggle={() => setOauthToast(null)}
        title={oauthToast ? oauthToast.title : ''}
        variant={oauthToast ? oauthToast.variant : 'primary'}
        position="top-center"
      >
        {oauthToast ? oauthToast.body : null}
      </Toast>
    </Container>
  )
}

export default LeadershipInAction
